<template>
  <div @contextmenu.stop.prevent>
    <div
      v-if="errorMessage"
      class="q-ma-xl text-bold text-red"
    >
      Error: {{ errorMessage }}
    </div>
    <highcharts
      v-else
      ref="chart"
      :options="highchartOptions"
      class="full-width"
    />
    <report-table-cell-menu
      ref="reportTableCellMenu"
      :report-element="reportElement"
      :report-table-column="yOutputColumn"
      :cell="selectedPoint"
      @edit-filter="field => editFilters(field)"
      @preview-changes="previewChanges()"
    />
  </div>
</template>

<script>

import { DEFAULT_CHART_OPTIONS } from 'src/models/report/ReportChart'
import { isComponentSourceCents } from 'src/models/report/ReportElement'
import ReportTableCellMenu from 'src/modules/admin/report-table/ReportTableCellMenu.vue'

export default {
  name: 'BubbleChart',
  components: {
    ReportTableCellMenu
  },
  props: {
    chart: {
      type: Object,
      required: true
    },
    reportElement: {
      type: Object,
      required: true
    },
    reportOptions: {
      type: Object,
      required: true
    }
  },
  configuration: ['currentCircuit'],
  data () {
    return {
      categories: [],
      selectedPoint: {},
      selectedColumn: {}
    }
  },
  computed: {
    errorMessage () {
      if (this.reportOptions.group_by.length === 0) return 'Chart requires grouping'

      if (this.reportOptions.group_by.length > 2) return 'Chart only supports up to two grouping: ' + this.reportOptions.group_by.length

      return null
    },
    singleGrouping () {
      return this.reportOptions.group_by.length === 1
    },
    series () {
      const rows = this.chart.chartData.table_data
      let yFactor = 1
      if (this.yOutputColumn && isComponentSourceCents(this.yOutputColumn.component)
      ) {
        yFactor = 100
      }
      let xFactor = 1
      if (this.xOutputColumn && isComponentSourceCents(this.xOutputColumn.component)
      ) {
        xFactor = 100
      }
      let zFactor = 1
      if (this.zOutputColumn && isComponentSourceCents(this.zOutputColumn.component)
      ) {
        zFactor = 100
      }

      let data
      if (this.reportOptions.group_by.length === 1) {
        const currentSeries = []
        data = _.map(rows, row => {
          const groupValue = row[this.reportOptions.group_by[0]]
          return {
            y: row[this.reportElement.finalOptions.y_axis_output_type] / yFactor,
            x: row[this.reportElement.finalOptions.x_axis_output_type] / xFactor,
            z: row[this.reportElement.finalOptions.size_of_bubble],
            custom: {
              tooltipCategory: groupValue.value || groupValue
            }
          }
        })

        if (this.reportElement.finalOptions.top_n) {
          data = _.sortBy(data, point => -(point.y || 0))
          data = data.splice(0, this.reportElement.finalOptions.top_n)
        } else if (this.reportElement.finalOptions.sort_by) {
          data = _.sortBy(data, point => point.sortValue)
          if (this.reportElement.finalOptions.descending) {
            data = data.reverse()
          }
        } else {
          data = _.sortBy(data, point => point.sortValue)
        }
        _.forEach(data, (eachDataPoint) => {
          const seriesInformation = {
            name: eachDataPoint.custom.tooltipCategory,
            data: [eachDataPoint]
          }
          currentSeries.push(seriesInformation)
        })

        return currentSeries
      } else if (this.reportOptions.group_by.length === 2) {
        const categoryField = this.reportOptions.group_by[0]
        const categoryValues = _(rows).map(categoryField).uniqBy(point => point.value || point).value()

        const seriesField = this.reportOptions.group_by[1]
        const seriesValues = _(rows).map(seriesField).uniqBy(point => point.value || point).value()

        const seriesArray = []

        _.each(categoryValues, categoryValue => {
          const seriesData = []
          let seriesName = ''
          const series = {
            data: []
          }
          _.each(seriesValues, seriesValue => {
            const answers = _(rows)
              .filter(row => _.isEqual(row[categoryField], categoryValue) &&
                _.isEqual(row[seriesField], seriesValue))

            const getTransformedValues = (field, factor) =>
              answers
                .map(this.reportElement.finalOptions[field])
                .map(value => value / factor)
                .value()

            const yValue = getTransformedValues('y_axis_output_type', yFactor)
            const xValue = getTransformedValues('x_axis_output_type', xFactor)
            const zValue = getTransformedValues('size_of_bubble', zFactor)

            if (!_.isEmpty(yValue)) {
              seriesData.push({
                x: xValue[0],
                y: yValue[0],
                z: zValue[0],
                custom: {
                  tooltipCategory: seriesValue.value || seriesValue
                }

              })
              seriesName = categoryValue.value || categoryValue
            }
          })

          series.data = seriesData
          series.name = seriesName
          seriesArray.push(series)
        })

        return seriesArray
      } else {
        return []
      }
    },
    yOutputColumn () {
      return _.find(this.reportElement.columns, { field: this.reportElement.finalOptions.y_axis_output_type })
    },
    xOutputColumn () {
      return _.find(this.reportElement.columns, { field: this.reportElement.finalOptions.x_axis_output_type })
    },
    zOutputColumn () {
      return _.find(this.reportElement.columns, { field: this.reportElement.finalOptions.size_of_bubble })
    },
    isXMoneyOutput () {
      return this.xOutputColumn && _.includes(this.xOutputColumn.component, 'money')
    },
    isXPercentOutput () {
      return this.xOutputColumn && _.includes(this.xOutputColumn.component, 'percent')
    },
    isYMoneyOutput () {
      return this.yOutputColumn && _.includes(this.yOutputColumn.component, 'money')
    },
    isYPercentOutput () {
      return this.yOutputColumn && _.includes(this.yOutputColumn.component, 'percent')
    },
    yOutputLabel () {
      let label = this.yOutputColumn.label

      if (this.isYMoneyOutput) {
        label += ` (${this.currentCircuit?.localeCurrencySymbol || '$'})`
      } else if (this.isYPercentOutput) {
        label += ' (%)'
      }
      return label
    },
    xOutputLabel () {
      let label = this.xOutputColumn.label
      if (this.isXMoneyOutput) {
        label += ` (${this.currentCircuit?.localeCurrencySymbol || '$'})`
      } else if (this.isXPercentOutput) {
        label += ' (%)'
      }
      return label
    },
    highchartOptions () {
      return {
        ...DEFAULT_CHART_OPTIONS(),
        chart: {

          type: 'bubble',
          plotBorderWidth: 1,
          zooming: {
            type: 'xy'
          },

          zoomType: 'x',
          resetZoomButton: {
            position: {
              align: 'right',
              verticalAlign: 'top',
              x: -10,
              y: 5
            },
            relativeTo: 'chart'
          },
          panning: true,
          panKey: 'shift',
          height: this.reportOptions.height
        },
        accessibility: {
          point: {
            valueDescriptionFormat: '{index}. {point.custom.tooltipCategory} ' +
            this.xOutputLabel + ' {point.x} ' +
            this.yOutputLabel + ' {point.y} ' +
            this.zOutputLabel + ' {point.z} '
          }
        },
        plotOptions: {
          column: {
            colorByPoint: this.singleGrouping
          },
          series: {
            dataLabels: {
              enabled: true,
              format: '{point.name}'
            }
          }

        },
        series: this.series,
        tooltip: {
          valueDecimals: 2,
          useHTML: true,
          pointFormat: ' <b> {point.custom.tooltipCategory} </b> ' +
            ' <br/>' + this.xOutputLabel + '' + ' {point.x}  ' +
            ' <br/>' + this.yOutputLabel + '' + ' {point.y}  ' +
            ' <br/>' + this.zOutputColumn.label + '' + ' {point.z}  ',
          followPointer: false
        },
        xAxis: {
          gridLineWidth: 1,
          title: {
            text: this.xOutputLabel
          }
        },
        yAxis: {
          title: {
            text: this.yOutputLabel
          }
        },
        legend: {
          enabled: this.reportOptions.show_legend,
          title: {
            text: '<div class="text-capitalize">' + this.reportOptions.group_by[0] + '</div>' +
              '<br/><span class = "text-caption text-grey-7"' +
             '>(Click to hide)</span>',
            style: {
              fontStyle: 'italic'
            }
          },
          verticalAlign: 'bottom',
          itemMarginTop: 5
        }

      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
